import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  convertir,
  esMoneda,
  formatear,
  monedaOpuesta,
  redondearCotizacion,
  type Moneda,
} from "@tora/shared";

/**
 * De donde sale la cotizacion vigente.
 *  - AUTO: la trajo el sistema de una fuente externa.
 *  - MANUAL: la cargo una persona a mano y pisa a la automatica.
 *
 * La distincion no es cosmetica: una cotizacion MANUAL vieja es la forma mas
 * facil de facturar todo mal durante un dia entero sin darse cuenta.
 */
export type OrigenCotizacion = "AUTO" | "MANUAL";

export interface CotizacionVigente {
  /** ARS por 1 USD. Ej: 1450 = un dolar vale $1.450. */
  valor: number;
  origen: OrigenCotizacion;
  /** ISO 8601 del momento en que se fijo. */
  actualizada: string;
  /** De donde salio: "Dolar blue", "Carga manual", etc. */
  fuente?: string;
}

interface MonedaContextValue {
  /** Moneda en la que se MUESTRAN los totales. No cambia lo persistido. */
  moneda: Moneda;
  setMoneda: (m: Moneda) => void;
  alternar: () => void;
  cotizacion: CotizacionVigente;
  setCotizacion: (c: CotizacionVigente) => void;
  /** Lleva un monto a la moneda de visualizacion con la cotizacion vigente. */
  enVista: (monto: number, desde: Moneda) => number;
  /** Idem, ya formateado. */
  formatearEnVista: (monto: number, desde: Moneda) => string;
}

const MonedaContext = createContext<MonedaContextValue | undefined>(undefined);

const CLAVE_STORAGE = "tora-moneda-vista";

/** Cotizacion de arranque hasta que llegue la real del backend. */
export const COTIZACION_INICIAL: CotizacionVigente = {
  valor: 1,
  origen: "MANUAL",
  actualizada: new Date(0).toISOString(),
  fuente: "Sin cotizacion cargada",
};

function leerMonedaPersistida(porDefecto: Moneda): Moneda {
  if (typeof window === "undefined") return porDefecto;
  try {
    const guardada = localStorage.getItem(CLAVE_STORAGE);
    return esMoneda(guardada) ? guardada : porDefecto;
  } catch {
    return porDefecto;
  }
}

export interface MonedaProviderProps {
  children: ReactNode;
  /** ARS por defecto: el negocio esta en Argentina y factura en pesos. */
  monedaInicial?: Moneda;
  cotizacionInicial?: CotizacionVigente;
  /** Desactiva la persistencia (util en tests). */
  persistir?: boolean;
}

export function MonedaProvider({
  children,
  monedaInicial = "ARS",
  cotizacionInicial = COTIZACION_INICIAL,
  persistir = true,
}: MonedaProviderProps): JSX.Element {
  const [moneda, setMonedaState] = useState<Moneda>(() =>
    persistir ? leerMonedaPersistida(monedaInicial) : monedaInicial,
  );
  const [cotizacion, setCotizacionState] = useState<CotizacionVigente>(cotizacionInicial);

  const setMoneda = useCallback(
    (m: Moneda) => {
      setMonedaState(m);
      if (!persistir) return;
      try {
        localStorage.setItem(CLAVE_STORAGE, m);
      } catch {
        // localStorage no disponible: la eleccion vive solo en esta sesion.
      }
    },
    [persistir],
  );

  const alternar = useCallback(() => {
    setMoneda(monedaOpuesta(moneda));
  }, [moneda, setMoneda]);

  const setCotizacion = useCallback((c: CotizacionVigente) => {
    setCotizacionState({ ...c, valor: redondearCotizacion(c.valor) });
  }, []);

  const value = useMemo<MonedaContextValue>(() => {
    const enVista = (monto: number, desde: Moneda): number =>
      convertir(monto, desde, moneda, cotizacion.valor);
    return {
      moneda,
      setMoneda,
      alternar,
      cotizacion,
      setCotizacion,
      enVista,
      formatearEnVista: (monto, desde) => formatear(enVista(monto, desde), moneda),
    };
  }, [moneda, setMoneda, alternar, cotizacion, setCotizacion]);

  return <MonedaContext.Provider value={value}>{children}</MonedaContext.Provider>;
}

export function useMoneda(): MonedaContextValue {
  const ctx = useContext(MonedaContext);
  if (!ctx) {
    throw new Error("useMoneda tiene que usarse dentro de <MonedaProvider>.");
  }
  return ctx;
}
